import React, { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTimes,
  faChevronRight,
  faChevronLeft,
  faCheckCircle,
  faLightbulb,
  faClone,
  faCheck,
} from "@fortawesome/free-solid-svg-icons";
import {
  faGoogle,
  faApple,
  faMicrosoft,
  faYahoo,
  faAndroid,
} from "@fortawesome/free-brands-svg-icons";
import DOMPurify from "dompurify";
import { useTranslation } from "../../i18n/LanguageContext";
import "./css/HelpGuideModal.css";

/**
 * HelpGuideModal â€” Contextual step-by-step micro-tutorial modal.
 *
 * LMS-inspired guided walkthrough (Workday / Salesforce Trailhead pattern):
 * - Tabbed by platform/context (e.g. Gmail vs Outlook vs Apple Mail)
 * - Numbered stepper with progressive disclosure
 * - Copyable values (email addresses, phone numbers)
 * - Final CTA to navigate to the feature
 *
 * @param {object}   guide        - The helpGuide data object for this item
 * @param {function} onClose      - Close the modal
 * @param {function} onNavigate   - Navigate to the feature (optional CTA)
 * @param {string}   actionLabel  - Label for the CTA button (e.g. "View My Analyses")
 */
function HelpGuideModal({ guide, onClose, onNavigate, actionLabel }) {
  const [activePlatformIndex, setActivePlatformIndex] = useState(0);
  const [activeStep, setActiveStep] = useState(0);
  const [copiedValue, setCopiedValue] = useState(null);
  const { t } = useTranslation();

  const activePlatform = guide.platforms[activePlatformIndex];
  const totalSteps = activePlatform.steps.length;
  const isLastStep = activeStep === totalSteps - 1;
  const showFinalStep = activeStep === totalSteps; // virtual "done" step

  // Reset step when switching platforms
  useEffect(() => {
    setActiveStep(0);
  }, [activePlatformIndex]);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  const handleCopy = useCallback(async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedValue(text);
      setTimeout(() => setCopiedValue(null), 2000);
    } catch {
      // Fallback for older browsers
      const textarea = document.createElement("textarea");
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
      setCopiedValue(text);
      setTimeout(() => setCopiedValue(null), 2000);
    }
  }, []);

  const handleNext = () => {
    if (showFinalStep) return;
    if (isLastStep) {
      setActiveStep(totalSteps); // go to final CTA step
    } else {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep((prev) => prev - 1);
    }
  };

  const handleStepClick = (index) => {
    setActiveStep(index);
  };

  const getPlatformIcon = (platformName) => {
    const name = platformName.toLowerCase();
    if (name.includes("gmail") || name.includes("google")) return faGoogle;
    if (
      name.includes("apple") ||
      name.includes("iphone") ||
      name.includes("ios")
    )
      return faApple;
    if (name.includes("outlook") || name.includes("microsoft"))
      return faMicrosoft;
    if (name.includes("yahoo")) return faYahoo;
    if (name.includes("android") || name.includes("samsung")) return faAndroid;
    return null;
  };

  /**
   * Renders step text, optionally highlighting a key phrase.
   * Pass copyable=true to also show a copy-to-clipboard button next to the highlight.
   */
  const renderStepText = (text, highlight, copyable = false) => {
    if (!highlight) {
      return (
        <span
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(text, {
              ALLOWED_TAGS: ["strong", "em", "u", "br", "code", "img"],
              ALLOWED_ATTR: ["src", "alt", "style"],
            }),
          }}
        />
      );
    }

    // Highlight is NOT embedded in the instruction text â€” append it after
    if (!text.includes(highlight)) {
      return (
        <>
          <span
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(text, {
                ALLOWED_TAGS: ["strong", "em", "u", "br", "code", "img"],
                ALLOWED_ATTR: ["src", "alt", "style"],
              }),
            }}
          />{" "}
          <span
            className={copyable ? "helpguide-copyable" : "helpguide-highlight"}
          >
            <code>{highlight}</code>
            {copyable && (
              <button
                className="helpguide-copy-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  handleCopy(highlight);
                }}
                title={t("helpGuide.modal.copyToClipboard")}
                aria-label={`${t("helpGuide.modal.copyToClipboard")} ${highlight}`}
              >
                <FontAwesomeIcon
                  icon={copiedValue === highlight ? faCheck : faClone}
                />
                {copiedValue === highlight
                  ? t("helpGuide.modal.copied")
                  : t("helpGuide.modal.copy")}
              </button>
            )}
          </span>
        </>
      );
    }

    // Highlight IS embedded in the instruction text â€” split and inline it
    const parts = text.split(highlight);
    return (
      <>
        <span
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(parts[0], {
              ALLOWED_TAGS: ["strong", "em", "u", "br", "code", "img"],
              ALLOWED_ATTR: ["src", "alt", "style"],
            }),
          }}
        />
        <span
          className={copyable ? "helpguide-copyable" : "helpguide-highlight"}
        >
          <code>{highlight}</code>
          {copyable && (
            <button
              className="helpguide-copy-btn"
              onClick={(e) => {
                e.stopPropagation();
                handleCopy(highlight);
              }}
              title={t("helpGuide.modal.copyToClipboard")}
              aria-label={`${t("helpGuide.modal.copyToClipboard")} ${highlight}`}
            >
              <FontAwesomeIcon
                icon={copiedValue === highlight ? faCheck : faClone}
              />
              {copiedValue === highlight
                ? t("helpGuide.modal.copied")
                : t("helpGuide.modal.copy")}
            </button>
          )}
        </span>
        <span
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(parts.slice(1).join(highlight), {
              ALLOWED_TAGS: ["strong", "em", "u", "br", "code", "img"],
              ALLOWED_ATTR: ["src", "alt", "style"],
            }),
          }}
        />
      </>
    );
  };

  return createPortal(
    <div
      className="helpguide-overlay"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={guide.title}
    >
      <div className="helpguide-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="helpguide-header">
          <div className="helpguide-header-text">
            <h2>{guide.title}</h2>
            {guide.subtitle && (
              <p className="helpguide-header-subtitle">{guide.subtitle}</p>
            )}
          </div>
          <button
            className="helpguide-close-btn"
            onClick={onClose}
            aria-label={t("helpGuide.modal.closeGuide")}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* Platform Tabs â€” only show if more than one platform */}
        {guide.platforms.length > 1 && (
          <div className="helpguide-platform-tabs" role="tablist">
            {guide.platforms.map((platform, index) => {
              const platformIcon = getPlatformIcon(platform.name);
              return (
                <button
                  key={platform.name}
                  className={`helpguide-platform-tab ${
                    index === activePlatformIndex ? "active" : ""
                  }`}
                  onClick={() => setActivePlatformIndex(index)}
                  role="tab"
                  aria-selected={index === activePlatformIndex}
                >
                  {platformIcon && (
                    <FontAwesomeIcon
                      icon={platformIcon}
                      className="helpguide-platform-icon"
                    />
                  )}
                  {platform.name}
                </button>
              );
            })}
          </div>
        )}

        {/* Step Progress Indicator */}
        <div className="helpguide-progress">
          {activePlatform.steps.map((_, index) => (
            <button
              key={index}
              className={`helpguide-progress-dot ${
                index === activeStep
                  ? "active"
                  : index < activeStep
                    ? "completed"
                    : ""
              }`}
              onClick={() => handleStepClick(index)}
              aria-label={`${t("helpGuide.modal.stepLabel")} ${index + 1}`}
              title={`${t("helpGuide.modal.stepLabel")} ${index + 1}`}
            >
              {index < activeStep ? (
                <FontAwesomeIcon icon={faCheckCircle} />
              ) : (
                <span>{index + 1}</span>
              )}
            </button>
          ))}
        </div>

        {/* Step Content */}
        <div className="helpguide-body">
          {!showFinalStep ? (
            <div
              className="helpguide-step"
              key={`${activePlatformIndex}-${activeStep}`}
            >
              <div className="helpguide-step-number">
                {t("helpGuide.modal.stepLabel")} {activeStep + 1}{" "}
                {t("helpGuide.modal.of")} {totalSteps}
              </div>
              <div className="helpguide-step-instruction">
                {renderStepText(
                  activePlatform.steps[activeStep].instruction,
                  activePlatform.steps[activeStep].highlight,
                  activePlatform.steps[activeStep].copyable,
                )}
              </div>
              {activePlatform.steps[activeStep].detail && (
                <div className="helpguide-step-detail">
                  <FontAwesomeIcon
                    icon={faLightbulb}
                    className="helpguide-tip-icon"
                  />
                  <span
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(
                        activePlatform.steps[activeStep].detail,
                        {
                          ALLOWED_TAGS: [
                            "strong",
                            "em",
                            "u",
                            "br",
                            "code",
                            "img",
                          ],
                          ALLOWED_ATTR: ["src", "alt", "style"],
                        },
                      ),
                    }}
                  />
                </div>
              )}
            </div>
          ) : (
            /* Final / Completion Step */
            <div className="helpguide-final-step">
              <div className="helpguide-final-icon">
                <FontAwesomeIcon icon={faCheckCircle} />
              </div>
              <h3>{t("helpGuide.modal.allSet")}</h3>
              <p
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(
                    activePlatform.finalStep || guide.finalStep,
                    {
                      ALLOWED_TAGS: ["strong", "em", "u", "br", "code", "img"],
                      ALLOWED_ATTR: ["src", "alt", "style"],
                    },
                  ),
                }}
              />
              {onNavigate && (
                <button
                  className="helpguide-cta-btn"
                  onClick={() => {
                    onNavigate(activePlatform.navigateAction || null);
                    onClose();
                  }}
                >
                  {activePlatform.actionLabel ||
                    actionLabel ||
                    t("helpGuide.modal.goThereNow")}{" "}
                  â†’
                </button>
              )}
            </div>
          )}
        </div>

        {/* Footer Navigation */}
        <div className="helpguide-footer">
          <button
            className="helpguide-nav-btn helpguide-nav-back"
            onClick={handleBack}
            disabled={activeStep === 0}
          >
            <FontAwesomeIcon icon={faChevronLeft} />
            {t("helpGuide.modal.back")}
          </button>

          <span className="helpguide-step-counter">
            {showFinalStep
              ? t("helpGuide.modal.complete")
              : `${activeStep + 1} / ${totalSteps}`}
          </span>

          {!showFinalStep && (
            <button
              className="helpguide-nav-btn helpguide-nav-next"
              onClick={handleNext}
            >
              {isLastStep
                ? t("helpGuide.modal.finish")
                : t("helpGuide.modal.next")}
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          )}

          {showFinalStep && (
            <button
              className="helpguide-nav-btn helpguide-nav-next"
              onClick={onClose}
            >
              {t("common.close")}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default HelpGuideModal;
